import { useTheme } from '@mui/material/styles'
import * as React from 'react'
import { useMemo } from 'react'
import { useFormikContext } from 'formik'

import { CheckboxLabel } from './atoms'

import type {
  EditableCustomFieldProps,
  ICustomFieldValueFormModel,
} from './typings'
import { MuiValidationIcon } from '@sales-center/components'
import { useHideOptional } from './useHideOptional'
import { useGetError } from './useGetError'
import FormControl from '@mui/material/FormControl'
import Checkbox from '@mui/material/Checkbox'
import FormHelperText from '@mui/material/FormHelperText'

export function EditableCustomCheckbox(props: EditableCustomFieldProps) {
  const {
    margin,
    field: { id: fieldId, name },
    fieldSetting: { required } = {},
    whiteBackground = false,
  } = props

  const {
    values: {
      customFields: { bool },
    },
    setFieldValue,
  } = useFormikContext<ICustomFieldValueFormModel>()

  const value = bool[fieldId]?.value ?? false

  const formattedName = useMemo(
    () =>
      required ? (
        <>
          {name}&nbsp;
          <MuiValidationIcon showError={false} />
        </>
      ) : (
        name
      ),
    [required, name],
  )

  const formikId = useMemo(() => `customFields.bool[${fieldId}]`, [fieldId])

  const onChange = React.useCallback(
    (_e, checked: boolean) => setFieldValue(`${formikId}.value`, checked),
    [formikId, setFieldValue],
  )

  const { hasError: fieldError, errorText } = useGetError(props)

  const showField = useHideOptional(props)

  const theme = useTheme()

  if (!showField) return null

  return (
    <FormControl fullWidth margin={margin}>
      <CheckboxLabel
        label={formattedName}
        value={value}
        onChange={onChange}
        control={
          <Checkbox
            checked={value}
            inputProps={{ name: formikId }}
            sx={{
              padding: theme.spacing(0),
              backgroundColor: whiteBackground
                ? theme.palette.common.white
                : 'inherit',
            }}
          />
        }
        data-test-id={`custom-checkbox-field-${fieldId}`}
      />
      {fieldError && (
        <FormHelperText id={`${formikId}-helper-text`} error>
          {errorText}
        </FormHelperText>
      )}
    </FormControl>
  )
}
